import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { currentFilterReducer } from '@common/redux/reducers/CurrentFilterReducer';
import { negotiationCountReducer } from '@common/redux/reducers/NegotiationCountReducer';
import { customCountListReducer } from '@common/redux/reducers/CustomCountListReducer';
import { ReportTableRowReducer } from '@common/redux/reducers/ReportTableRowReducer';
import { SearchResultReducer } from '@common/redux/reducers/SearchResultReducer';
import { StoreSelectorReducer } from '@common/redux/reducers/StoreSelectorReducer';
import { NegotiationPeriodReducer } from '@common/redux/reducers/NegotiationPeriodReducer';
import { NegotiationPendingReducer } from '@common/redux/reducers/NegotiationPendingReducer';
import { SearchConditionReducer } from '@common/redux/reducers/SearchConditionReducer';
import { NegotiationNextActionReducer } from '@common/redux/reducers/NegotiationNextActionReducer';
import { NegotiationProbReducer } from '@common/redux/reducers/NegotiationProbReducer';
import { NegotiationResultReducer } from '@common/redux/reducers/NegotiationResultReducer';
import { RefreshReducer } from '@common/redux/reducers/RefreshReducer';
import { FilterListReducer } from '@common/redux/reducers/FilterListReducer';
import { LoadingStatusReducer } from '@common/redux/reducers/LoadingStatusReducer';
import { MailTopReducer } from '@common/redux/reducers/MailTopReducer';
import { searchBranchNextActionReducer } from '@common/redux/reducers/SearchBranchNextActionReducer';
import { searchBranchPendingReducer } from '@common/redux/reducers/SearchBranchPendingReducer';
import { searchBranchPeriodReducer } from '@common/redux/reducers/SearchBranchPeriodReducer';
import { searchBranchProbReducer } from '@common/redux/reducers/SearchBranchProbReducer';
import { searchBranchResultReducer } from '@common/redux/reducers/SearchBranchResultReducer';
import { searchBranchStatusReducer } from '@common/redux/reducers/SearchBranchStatusReducer';
import { searchBranchCommodityCategoryContractReducer } from '@common/redux/reducers/SearchBranchCommodityCategoryContractReducer';
import { MailDetailResultReducer } from '@common/redux/reducers/MailDetailResultReducer';
import { AppState } from '@common/redux/modules/store';
import { UserReducer, CustomerUserState } from '../reducers/UserReducer';
import { SorryMessageReducer } from '@common/redux/reducers/SorryMessageReducer';
import { MailTemplateReducer } from '@common/redux/reducers/MailTemplateReducer';
import { ApplyStaffReducer } from '@common/redux/reducers/ApplyStaffReducer';
import { MailTemplateApplyReducer } from '@common/redux/reducers/MailTemplateApplyReducer';
import { MailCreateReducer } from '@common/redux/reducers/MailCreateReducer';
import { MailDraftReducer } from '@common/redux/reducers/MailDraftReducer';
import { MailSaveReducer } from '@common/redux/reducers/MailSaveReducer';
import { MailAddressSelectReducer } from '@common/redux/reducers/MailAddressSelectReducer';
import { MailTemplateDetailReducer } from '@common/redux/reducers/MailTemplateDetailReducer';
import { MailStopReducer } from '@common/redux/reducers/MailStopReducer';
import { MailStopDoneReducer } from '@common/redux/reducers/MailStopDoneReducer';
import { MasterDetailReducer } from '@common/redux/reducers/MasterDetailReducer';
import { SearchCategoryReducer } from '@common/redux/reducers/SearchCategoryReducer';
import { NotificationStatusReducer } from '@common/redux/reducers/NotificationStatusReducer';
import { ErrorReducer } from '../reducers/ErrorReducer';
import { CommonResult } from '@common/external/Common';
import { MasterInfoReducer } from '@common/redux/reducers/MasterInfoReducer';
import { MailDeliveryStopReducer } from '@common/redux/reducers/MailDeliveryStopReducer';
import { DevConsoleReducer } from '@common/redux/reducers/DevConsoleReducer';
import { ConsentInfoReducer } from '@common/redux/reducers/ConsentInfoReducer';
import { OptinMailReducer, OptinMailState } from '../reducers/OptinMailReducer';
import { VisitReservationInfoReducer } from '@common/redux/reducers/VisitReservationInfoReducer';
import { CustomerInfoAllReducer } from '@common/redux/reducers/CustomerInfoAllReducer';

export type CustomerAppState = AppState & {
  loginUser: CustomerUserState;
  error: CommonResult;
  optinMail: OptinMailState;
};

const store = createStore(
  combineReducers<CustomerAppState>({
    negotiationCount: negotiationCountReducer,
    customCountList: customCountListReducer,
    negotiationPeriod: NegotiationPeriodReducer,
    negotiationProb: NegotiationProbReducer,
    negotiationNextAction: NegotiationNextActionReducer,
    reportTableRow: ReportTableRowReducer,
    currentFilterCondition: currentFilterReducer,
    negotiationPending: NegotiationPendingReducer,
    negotiationResult: NegotiationResultReducer,
    searchBranchPeriod: searchBranchPeriodReducer,
    searchBranchProb: searchBranchProbReducer,
    searchBranchNextAction: searchBranchNextActionReducer,
    searchBranchPending: searchBranchPendingReducer,
    searchBranchResult: searchBranchResultReducer,
    searchBranchStatus: searchBranchStatusReducer,
    searchBranchCommodityCategoryContract: searchBranchCommodityCategoryContractReducer,
    searchResult: SearchResultReducer,
    SearchCondition: SearchConditionReducer,
    loginUser: UserReducer,
    storeSelector: StoreSelectorReducer,
    currentRefresh: RefreshReducer,
    filterList: FilterListReducer,
    notificationStatus: NotificationStatusReducer,
    loadingStatus: LoadingStatusReducer,
    mailTop: MailTopReducer,
    mailDetail: MailDetailResultReducer,
    mailTemplate: MailTemplateReducer,
    mailTemplateDetail: MailTemplateDetailReducer,
    applyStaffList: ApplyStaffReducer,
    mailTemplateApply: MailTemplateApplyReducer,
    mailCreate: MailCreateReducer,
    mailDraft: MailDraftReducer,
    mailSave: MailSaveReducer,
    mailAddressSelect: MailAddressSelectReducer,
    mailStop: MailStopReducer,
    mailDeliveryStop: MailDeliveryStopReducer,
    mailStopDone: MailStopDoneReducer,
    masterDetail: MasterDetailReducer,
    masterInfo: MasterInfoReducer,
    searchCategory: SearchCategoryReducer,
    sorryMessage: SorryMessageReducer,
    error: ErrorReducer,
    devConsole: DevConsoleReducer,
    consentInfo: ConsentInfoReducer,
    optinMail: OptinMailReducer,
    visitReservationInfo: VisitReservationInfoReducer,
    customerInfoAll: CustomerInfoAllReducer,
  }),
  compose(
    applyMiddleware(thunk),
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ ? (window as any).__REDUX_DEVTOOLS_EXTENSION__() : (f: any) => f
  )
);

store.subscribe(() => {
  // console.log(store.getState());
});

export default store;
